import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { inr, fmtDate } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Passbook } from "../../components/Passbook";
import { useAuth } from "../../context/AuthContext";
import { Wallet, ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle2 } from "lucide-react";

const Metric = ({ label, value, icon: Icon, i, mono = true }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.05 }}
    data-testid={`metric-${label.toLowerCase().replace(/ /g, "-")}`}
    className="bg-card border border-border p-6 rounded-sm hover:-translate-y-px hover:shadow-sm transition-all"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="overline">{label}</span>
      <Icon size={16} className="text-muted-foreground" />
    </div>
    <p className={`text-3xl font-bold tracking-tight ${mono ? "font-mono-num" : "font-heading"}`}>{value}</p>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  return (
    <div>
      <p className="overline">Overview</p>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">
        Welcome, {user?.name}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden mb-6">
        <div className="bg-[#09090B] text-white p-6 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <span className="overline text-white/50">Available Balance</span>
            <Wallet size={16} className="text-[#059669]" />
          </div>
          <p data-testid="available-balance" className="text-4xl font-bold font-mono-num tracking-tighter">
            {inr(data?.available_balance)}
          </p>
          <p className="text-xs text-white/40 mt-2 font-mono-num">On hold: {inr(data?.held_balance)}</p>
        </div>
        <Metric label="Total Deposit" value={inr(data?.total_deposit)} icon={ArrowDownToLine} i={1} />
        <Metric label="Total Withdrawal" value={inr(data?.total_withdrawal)} icon={ArrowUpFromLine} i={2} />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-10">
        <Metric label="Pending Transactions" value={data?.pending ?? "—"} icon={Clock} i={3} mono={false} />
        <Metric label="Successful Transactions" value={data?.success ?? "—"} icon={CheckCircle2} i={4} mono={false} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-xl tracking-tight">Recent Transactions</h2>
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-card">
        {(data?.recent || []).length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No transactions yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="recent-transactions">
            <thead>
              <tr className="text-left overline border-b border-border">
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Amount</th>
                <th className="px-5 py-3 font-semibold hidden sm:table-cell">UTR</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Date</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((t) => (
                <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-black/5 transition-colors">
                  <td className="px-5 py-3 capitalize">{t.type}</td>
                  <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                  <td className="px-5 py-3 font-mono-num text-xs hidden sm:table-cell">{t.utr || "—"}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                  <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-10">
        <Passbook />
      </div>
    </div>
  );
}
